import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";

import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef, useState } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { format } from "date-fns";
import { ReportType, ReportTypeEnum } from "../../types";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { MonthlySalesReportTypes } from "@repo/core/types/reports";
import { useQuery } from "@tanstack/react-query";

const reportsResource = new ReportsResource(newApInstance);

export const MonthlySalesReportController = () => {
  const { isMobile } = useDimensions();

  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const [selectedReportType, setSelectedReportType] =
    useState<MonthlySalesReportTypes>(ReportTypeEnum.SALES);

  const filterModalRef = useRef<IModalRefProps>(null);

  const {
    data: monthlySalesReportsResponse,
    isFetching: isFetchingMonthlySalesReports
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "monthlySalesReport",
      filterStartDate,
      filterEndDate,
      selectedStore,
      selectedProduct,
      selectedAttendant,
      selectedReportType
    ],
    queryFn: () =>
      reportsResource.getMonthlySalesReports({
        startDate: format(filterStartDate, "yyyy-MM-dd"),
        endDate: format(filterEndDate, "yyyy-MM-dd"),
        store: [selectedStore],
        product: selectedProduct,
        attendant: selectedAttendant,
        type: selectedReportType
      })
  });

  const columnHelper = createColumnHelper();

  const onSelectReportType = (reportType: ReportTypeEnum) => {
    setSelectedReportType(reportType as MonthlySalesReportTypes);
  };

  const rightLabelTransformByReportType: Record<
    MonthlySalesReportTypes,
    (value: number) => string
  > = {
    [ReportTypeEnum.TICKETS]: (value: number) => (value / 50).toString(),
    [ReportTypeEnum.SALES]: (value: number) => value.toString()
  };

  const reportTypes: ReportType[] = [
    {
      title: "Vendas",
      type: ReportTypeEnum.SALES
    },
    {
      title: "Ticket Médio",
      type: ReportTypeEnum.TICKETS
    }
  ];

  const headerColumns = monthlySalesReportsResponse?.data?.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const {
    tableData,
    chartBarData,
    chartLineData,
    cardsData,
    hasReportData,
    rightLabelsTransform
  } = useMemo(() => {
    const tableData = convertDataRows(
      monthlySalesReportsResponse?.data?.table.headers,
      monthlySalesReportsResponse?.data?.table.rows
    );

    const chartBarData = monthlySalesReportsResponse?.data?.charts?.find(
      (chart) => chart.type === "bar"
    );

    const chartLineData = monthlySalesReportsResponse?.data?.charts?.find(
      (chart) => chart.type === "line"
    );

    const cardsData = monthlySalesReportsResponse?.data?.mobileTable;

    const hasReportData =
      Array.isArray(monthlySalesReportsResponse?.data?.table.rows) &&
      monthlySalesReportsResponse.data.table.rows.length > 0;

    const rightLabelsTransform =
      rightLabelTransformByReportType[selectedReportType];

    return {
      tableData,
      chartBarData,
      chartLineData,
      cardsData,
      hasReportData,
      rightLabelsTransform
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlySalesReportsResponse?.data, selectedReportType]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      "Relatório_Vendas_Mensais",
      monthlySalesReportsResponse?.data?.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    filterModalRef,
    topRightText,
    reportTypes,
    selectedReportType,
    onSelectReportType,
    isFetchingMonthlySalesReports,
    chartBarData,
    chartLineData,
    cardsData,
    filterStartDate,
    filterEndDate,
    hasReportData,
    handleSelectFilters,
    rightLabelsTransform
  };
};
